import { v4 as uuidv4 } from "uuid";

import { combineFilters } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { isFilterEmpty } from "@formant/core/common/isFilterEmpty";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IDashboardModule } from "@formant/core/model/IDashboardModule";
import { IObserveLayout } from "@formant/core/model/IObserveLayout";
import { IScopeFilter } from "@formant/core/model/IScopeFilter";
import { IView } from "@formant/core/model/IView";
import { SectionModeEnum } from "@formant/core/model/SectionMode";
import { getColorFromColorSet } from "@formant/core/model/util/getColorFromColorSet";
import {
    IStreamsConfiguration,
    ModuleComponentConfigCollection,
    ModulesId,
    NumericComponentConfiguration,
    StreamSource,
} from "@formant/rectangle-sdk";
import { IDashboardView } from "@formant/views-editor/src/types/EditorTypes";

import { metadataStore, streamStore, viewStore } from "../../../../stores";
import {
    generateCompleteLayout,
    IModuleInfo,
} from "../../../../utils/layoutHelpers";
import { generateStreamInfo } from "../../../Observe/Modules";
import { scaleV1RectToCoherenceGrid } from "./coherence-grid";

export const handleTelemetryMigration = async (
    view: DefinedEntity<IView>,
    deviceId: string | undefined,
) => {
    const end = new Date();
    // Mirror v1's metadata window (ObserveStore.metadataStart: one month
    // before the start of the current month) so the migrated view gets the
    // same module list v1 displays.
    const start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
    const context: IScopeFilter = {
        start: start.toISOString(),
        end: end.toISOString(),
    };

    // Scope the metadata query to the device being migrated so the new view
    // only gets modules for streams that device actually produced; without
    // this the query spans every device matching the view's tag filters.
    const mergedFilter: IScopeFilter = combineFilters(context, {
        ...view.filter,
        deviceIds: deviceId ? [deviceId] : view.filter?.deviceIds,
    });

    const queryMetadata = await metadataStore.listAsync(mergedFilter);
    let activeStreamInfos = generateStreamInfo(queryMetadata);
    activeStreamInfos = activeStreamInfos.filter(
        (streamInfo, index, self) =>
            index === self.findIndex(t => t.name === streamInfo.name),
    );

    // v1 titles modules with the org-level stream alias when one is set
    // (see Module.tsx / streamNameAliasOrNameMap), so migrated modules must
    // carry the same alias to keep their names. The stream registry also
    // provides types for the layout-only streams below.
    const streams = await streamStore.listAllAsync();
    const enabledStreams = streams.filter(_ => _.enabled);
    const aliasByStreamName = new Map(
        enabledStreams.map(_ => [_.streamName, _.alias || _.streamName]),
    );
    const streamTypeByName = new Map(
        enabledStreams.map(_ => [_.streamName, _.streamType]),
    );

    // For views without a filter, v1 also shows modules for streams saved in
    // the view layout that have no recent metadata (see Modules.tsx
    // layoutStreamInfos). Filtered views only show metadata-backed streams —
    // layout leftovers from removed streams stay hidden.
    const viewLayout = (view.layout ?? {}) as IObserveLayout;
    const filtering = view.filter && !isFilterEmpty(view.filter);
    if (!filtering) {
        const activeNames = new Set(activeStreamInfos.map(_ => _.name));
        const layoutStreamInfos: IModuleInfo[] = Object.keys(viewLayout)
            .filter(
                name =>
                    !activeNames.has(name) &&
                    !name.startsWith("custom-module:") &&
                    !name.startsWith("analytics-module:") &&
                    streamTypeByName.has(name),
            )
            .map(name => ({
                name,
                type: defined(streamTypeByName.get(name)),
            }));
        activeStreamInfos = [...activeStreamInfos, ...layoutStreamInfos];
    }

    const visibleLayout = generateCompleteLayout(
        viewLayout,
        activeStreamInfos.map(_ => ({
            name: _.name,
            type: _.type,
        })),
    );

    const modules = await Promise.all(
        activeStreamInfos.map(async moduleInfo =>
            generateTelemetryModule(
                moduleInfo,
                visibleLayout,
                aliasByStreamName,
            ),
        ),
    );

    const modulesObject = modules
        .filter(module => module !== undefined)
        .reduce(
            (acc, module) => ({
                ...acc,
                [module.graph.id]: module,
            }),
            {},
        );

    return await createV2View(view, deviceId, modulesObject);
};

const createV2View = async (
    view: DefinedEntity<IView>,
    deviceId: string | undefined,
    modules: {
        [moduleId: string]: IDashboardModule;
    },
) => {
    const newView = {
        name: view.name + " - Coherence",
        layout: {
            modules,
        },
        // Fleet-level migrations keep the source view's scoping instead of
        // an empty device filter.
        filter: deviceId ? { deviceIds: [deviceId] } : (view.filter ?? {}),
        groupFilter: view.groupFilter ?? {},
        tags: view.tags,
        layoutType: "dashboard",
        deviceFilter: {},
        index: 0,
        configuration: [],
    } as any as IDashboardView;

    let createdView: DefinedEntity<IView>;
    try {
        createdView = await viewStore.createSimple(newView);
    } catch (error) {
        throw error;
    }
    return createdView;
};

const generateTelemetryModule = async (
    moduleInfo: IModuleInfo,
    layout: IObserveLayout,
    aliasByStreamName: Map<string, string>,
) => {
    const typeToModuleMap: { [key: string]: ModulesId } = {
        numeric: ModulesId.LINE_CHART,
        "numeric set": ModulesId.LINE_CHART,
        bitset: ModulesId.STATE_MONITOR,
        location: ModulesId.MAP,
        video: ModulesId.VIDEO,
        image: ModulesId.IMAGE,
        json: ModulesId.TEXT,
        // v1 renders text/log streams (e.g. PLC logs) as text modules; v2 has
        // the same text module.
        text: ModulesId.TEXT,
        health: ModulesId.TABLE_CHART,
        localization: ModulesId.THREE_D_SCENE,
        "point cloud": ModulesId.THREE_D_SCENE,
    };
    if (!typeToModuleMap[moduleInfo.type]) {
        return;
    }
    const positionInLayout = layout[moduleInfo.name];
    if (!positionInLayout) {
        return;
    }
    const moduleComponentConfig =
        ModuleComponentConfigCollection.autoConfigureModuleComponent(
            typeToModuleMap[moduleInfo.type],
        );

    const moduleId = uuidv4();
    const con = new ModuleComponentConfigCollection(moduleId);
    con.widgets.push(moduleComponentConfig);
    const nodeGraph = con.buildGraph();
    const displayName =
        aliasByStreamName.get(moduleInfo.name) || moduleInfo.name;
    nodeGraph.name = displayName;
    const scaledPosition = scaleV1RectToCoherenceGrid(
        positionInLayout,
        typeToModuleMap[moduleInfo.type] === ModulesId.LINE_CHART,
    );
    const moduleLayout = {
        name: displayName,
        graph: nodeGraph,
        x: scaledPosition.x,
        y: scaledPosition.y,
        width: scaledPosition.width,
        height: scaledPosition.height,
    };

    if (
        moduleInfo.type === "point cloud" ||
        moduleInfo.type === "localization"
    ) {
        return moduleLayout;
    }

    const streams: StreamSource[] = [];
    if (moduleInfo.type === "numeric") {
        streams.push({
            name: moduleInfo.name,
            path: moduleInfo.name,
            streamName: moduleInfo.name,
            customUnit: "",
            streamType: moduleInfo.type,
            type: "telemetry",
            color: getColorFromColorSet(new Set()),
        });
    } else if (moduleInfo.type === "numeric set") {
        if (!moduleInfo.id) {
            return;
        }
        const metadata = await metadataStore.getLatestValueForMetadataIdAsync(
            moduleInfo.id,
            365,
        );
        if (!metadata?.currentValue || !Array.isArray(metadata.currentValue)) {
            return;
        }

        const selectedColors = new Set<string>();
        metadata.currentValue.forEach(value => {
            const assignedColor = getColorFromColorSet(selectedColors);
            selectedColors.add(assignedColor);
            streams.push({
                // `name` is the series display label (extraction uses `path`);
                // v1 labels set members with just the member label.
                name: value.label,
                path: value.label,
                streamName: metadata.name,
                customUnit: value.unit,
                streamType: moduleInfo.type,
                type: "telemetry",
                color: assignedColor,
            });
        });
        // v1 always renders a numeric set as one mini chart per set member;
        // sectioning by data ("small multiple" in the v2 inspector) is the
        // equivalent layout in coherence.
        (con.widgets[0] as NumericComponentConfiguration).sectionBy =
            SectionModeEnum.BY_DATA;
    } else if (moduleInfo.type === "bitset") {
        if (!moduleInfo.id) {
            return;
        }
        const metadata = await metadataStore.getLatestValueForMetadataIdAsync(
            moduleInfo.id,
            365,
        );
        const currentValue = metadata?.currentValue as
            | { keys: string[]; values: string[] }
            | undefined;
        if (!currentValue?.keys) {
            return;
        }
        const selectedColors = new Set<string>();
        currentValue.keys.forEach(key => {
            const assignedColor = getColorFromColorSet(selectedColors);
            selectedColors.add(assignedColor);
            streams.push({
                // `name` is the row display label (extraction uses `path`);
                // v1 labels bitset rows with just the key.
                name: key,
                path: key,
                streamName: moduleInfo.name,
                customUnit: "",
                streamType: moduleInfo.type,
                type: "telemetry",
                color: assignedColor,
            });
        });
    } else {
        streams.push({
            name: moduleInfo.name,
            path: moduleInfo.name,
            streamName: moduleInfo.name,
            customUnit: "",
            streamType: moduleInfo.type,
            type: "telemetry",
            color: "",
        });
    }

    const widget = con.widgets[0] as IStreamsConfiguration;
    widget.streams = streams;
    moduleLayout.graph = con.buildGraph();
    return moduleLayout;
};
